import { Panel, SectionHeader, ErpTable, Th, Td, EmptyRow } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { stationRollups } from './api'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'

/**
 * Stations grid — ranked by outstanding count (§16.3), not percentage.
 *
 * Outstanding = registered − voted. The station with the most outstanding members
 * is the one the organiser needs to act on, not the one with the worst percentage
 * (because 400 people at 80% is 80 outstanding; 200 people at 95% is 10).
 */
export function StationsPage() {
  const canRead = useCan('turnout:read')
  const selection = useElection()
  const { electionId } = selection
  // `/turnout/stations` is not a route the server serves — the station grid IS the
  // per-station rollup, which is what carries members / voted / outstanding.
  const stations = useLoad(
    () => (electionId ? stationRollups(electionId) : Promise.resolve([])),
    [electionId])

  if (!canRead) return <Forbidden what="Turnout stations" />

  const rows = ((stations.data ?? []))
    .slice()
    .sort((a, b) => (b.outstandingCount as number) - (a.outstandingCount as number))

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Stations"
        description="Ranked by outstanding count — the stations that most need attention are first."
        right={<ElectionPicker selection={selection} />}
      />
      <ErrorStrip message={stations.error} />
      {stations.loading && rows.length === 0 ? <LoadingBlock label="Loading stations…" /> : (
        <Panel title="All stations">
          <ErpTable head={(
            <>
              <Th>Name</Th>
              <Th className="text-right">Registered</Th>
              <Th className="text-right">Cells</Th>
              <Th className="text-right">Active</Th>
              <Th className="text-right">Coordinator fill</Th>
              <Th className="text-right">Outstanding</Th>
              <Th>Queue</Th>
            </>
          )}>
            {rows.length === 0 ? (
              <EmptyRow colSpan={7} message="No stations linked to this election yet." />
            ) : rows.map((s) => (
              <tr key={s.id} className="hover:bg-muted/40">
                <Td>{s.unitName ?? s.unitId.slice(0, 8)}</Td>
                <Td className="text-right tabular-nums">{s.registeredCount.toLocaleString()}</Td>
                <Td className="text-right tabular-nums">{s.cellsTotal}</Td>
                <Td className="text-right tabular-nums">{s.cellsActive}</Td>
                {/* Derived, not served: the rollup carries the two counts and the ratio
                    is a rendering choice. A percentage on a two-cell station is noise,
                    so the counts are shown and the percentage only above a handful. */}
                <Td className="text-right tabular-nums">
                  {s.cellsTotal === 0 ? '—'
                    : `${s.cellsWithCoordinator}/${s.cellsTotal}`}
                </Td>
                <Td className="text-right tabular-nums font-medium">{s.outstandingCount.toLocaleString()}</Td>
                {/* The latest queue band lives on turnout_queue_report, not on the
                    rollup, and there is no endpoint joining the two yet. Rendering an
                    always-blank column reads as "no queue"; the honest cell says so. */}
                <Td className="text-muted-foreground">—</Td>
              </tr>
            ))}
          </ErpTable>
        </Panel>
      )}
    </div>
  )
}
